import { PublicClientApplication, InteractionStatus, EventType, InteractionRequiredAuthError } from '@azure/msal-browser';

const msalConfig = {
  auth: {
    clientId: process.env.REACT_APP_AZURE_AUTH_CLIENT_ID,
    authority: 'https://unicefpartners.b2clogin.com/unicefpartners.onmicrosoft.com/B2C_1_pd_thrive360',
    knownAuthorities: [
      'unicefpartners.b2clogin.com',
    ],
    redirectUri: window.location.origin + '/stock-performance',
    postLogoutRedirectUri: window.location.origin + '/stock-performance',
  },
  cache: {
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: false,
  },
};

const b2cPolicies = {
  names: {
    signUpSignIn: 'B2C_1_pd_thrive360',
    passwordReset: 'B2C_1_PasswordResetPolicy',
  },
  authorities: {
    signUpSignIn: {
      authority: `https://unicefpartners.b2clogin.com/unicefpartners.onmicrosoft.com/B2C_1_pd_thrive360`,
    },
    passwordReset: {
      authority: `https://unicefpartners.b2clogin.com/unicefpartners.onmicrosoft.com/B2C_1_PasswordResetPolicy`,
    },
  },
};

class AuthService {
  constructor() {
    this.msalInstance = null;
    this.b2cPolicies = b2cPolicies;
    this.initialized = false;
    this.initializationPromise = null;
  }

  async initialize() {
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.initializationPromise = new Promise(async (resolve, reject) => {
      try {
        if (!this.initialized) {
          this.msalInstance = new PublicClientApplication(msalConfig);
          await this.msalInstance.initialize();
          // Listen for MSAL login failures to catch B2C forgot-password trigger (AADB2C90118)
          this.msalInstance.addEventCallback((event) => {
            try {
              if (event?.eventType === EventType.LOGIN_FAILURE) {
                const message = String(event?.error?.message || event?.error || '');
                if (message.includes('AADB2C90118')) {
                  this.msalInstance.loginRedirect({
                    authority: this.b2cPolicies.authorities.passwordReset.authority,
                  });
                }
              }
            } catch (_) {
              // no-op
            }
          });
          this.initialized = true;
        }
        resolve(this.msalInstance);
      } catch (error) {
        console.error('Failed to initialize MSAL:', error);
        reject(error);
      }
    });

    return this.initializationPromise;
  }

  // Get MSAL instance
  async getMsalInstance() {
    try {
      await this.initialize();
      return this.msalInstance;
    } catch (error) {
      console.error('Error getting MSAL instance:', error);
      throw error;
    }
  }

  // Get B2C policies
  getB2CPolicies() {
    return this.b2cPolicies;
  }

  // Process user claims from token
  processUserClaims(claims) {
    if (!claims) {
      return {
        email: '',
        name: '',
        lastName: '',
        country: ''
      };
    }

    // Get email from emails array (first element)
    const emailClaim = claims.emails && claims.emails.length > 0 ? claims.emails[0] : '';
    
    // Try multiple possible claim names for country
    const countryClaim = claims.country || claims.extension_Country || claims.extension_country || claims.extension_CountryCode || '';
    
    return {
      email: emailClaim,
      name: claims.given_name || '',
      lastName: claims.family_name || '',
      country: countryClaim,
    };
  }

  // Extract user claims from MSAL accounts
  async extractUserClaims(instance, inProgress) {
    let userClaims = { email: '', name: '', lastName: '', country: '' };
    
    if (inProgress === 'none') {
      await this.initialize();
      const accounts = instance.getAllAccounts();
      if (accounts && accounts.length > 0) {
        const claims = accounts[0].idTokenClaims || {};
        userClaims = this.processUserClaims(claims);
      }
    }
    
    return userClaims;
  }

  // Handle B2C password reset
  async handlePasswordReset(instance) {
    await this.initialize();
    // Support both fragment and query params for error_description
    const hashParams = new URLSearchParams((window.location.hash || '').replace(/^#/, ''));
    const queryParams = new URLSearchParams(window.location.search || '');
    const error =
      hashParams.get('error_description') ||
      hashParams.get('error') ||
      queryParams.get('error_description') ||
      queryParams.get('error') ||
      '';
    if (error && String(error).includes('AADB2C90118')) {
      instance.loginRedirect({
        authority: this.b2cPolicies.authorities.passwordReset.authority,
      });
      return true;
    }
    return false;
  }

  async loginRedirect(instance, inProgress) {
    try {
      await this.initialize();
      
      if (inProgress === InteractionStatus.None) {
        await instance.loginRedirect({
          authority: this.b2cPolicies.authorities.signUpSignIn.authority,
        });
      } else {
        console.log("Login interaction already in progress");
      }
    } catch (error) {
      console.error('Login redirect failed:', error);
      throw error;
    }
  }
  
  // Logout redirect
  async logoutRedirect(instance) {
    await this.initialize();
    instance.logoutRedirect();
  }

  async getUserAccessOrCreate(claims) {
    const userService = (await import('./userService')).default;
    const access = await userService.checkUserAccess(claims.email);
    // If the user is not found in the DB but their token does not contain a country yet
    // defer user creation until they choose a country in the client-side country picker.
    if (!claims.country) {
      return access; // client will handle missing-country flow later
    }
    if (!access.hasAccess && !access.isApproved && access.message.startsWith('User not found')) {
      await userService.createUser(claims);          // create
      return await userService.checkUserAccess(claims.email); // single follow-up
    }
    return access;
  }
  // get ID token for the user to authenticate with the backend functions 
  async getIdToken() {
    // ensure msalInstance is created + initialized
    await this.initialize();
  
    const accounts = this.msalInstance.getAllAccounts();
    if (!accounts || accounts.length === 0) {
      // No cached session — force interactive login to establish one.
      await this.msalInstance.loginRedirect({
        authority: this.b2cPolicies.authorities.signUpSignIn.authority,
        scopes: ["openid", "offline_access"],
      });
      // Redirect will take over; return a never-resolving promise to halt callers.
      return new Promise(() => {});
    }
  
    const account = accounts[0];
  
    try {
      const result = await this.msalInstance.acquireTokenSilent({
        account,
        scopes: ["openid", "offline_access"],
      });
      return result.idToken;
    } catch (error) {
      // When no session exists in the IdP or consent is needed, MSAL throws
      // InteractionRequiredAuthError. Trigger an interactive redirect to recover.
      if (
        error instanceof InteractionRequiredAuthError ||
        ['no_tokens_found', 'token_renewal_error', 'interaction_required', 'monitor_window_timeout'].includes(error?.errorCode)
      ) {
        await this.msalInstance.acquireTokenRedirect({
          account,
          scopes: ["openid", "offline_access"],
          authority: this.b2cPolicies.authorities.signUpSignIn.authority,
        });
        return new Promise(() => {});
      }
      throw error;
    }
  }

  // Ensure user exists in the database after authentication
  async ensureUserInDatabase(userClaims, onCountryError) {
    if (!userClaims || !userClaims.email) return;
    
    try {
      const userService = (await import('./userService')).default;
      
      // First check if user exists by checking access
      const access = await userService.checkUserAccess(userClaims.email);
      
      const skip = access.hasAccess || access.isApproved ||
                   /pending|rejected|deactivated|not approved/i.test(access.message);

      if (skip) {
        return;
      }

      // User doesn't exist, create new user
      await userService.createUser(userClaims);
    } catch (err) {
      if (
        onCountryError &&
        err?.message &&
        (
          err.message.includes('Invalid country') ||
          err.message.includes('Country is required') ||
          err.message.includes('Email and country are required')
        )
      ) {
        onCountryError(userClaims);
        return;
      }
      console.error('Failed to handle user in DB:', err);
    }
  }
}

const authService = new AuthService();
export default authService; 